import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { deleteQuestion, getQuestionSetById, getAllQuestions } from '../../api/questionApi';
import {
  Typography,
  Box,
  IconButton,
  Avatar,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  Quiz as QuizIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  MenuBook as MenuBookIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { useToast } from '../../contexts/ToastContext';
import {
  PageHeader,
  ModernButton,
  ModernDataGrid,
  QuestionSetInfoCard,
  EmptyState,
} from '../../components/common';
import BreadcrumbsNav from '../../components/common/BreadcrumbsNav';
import ConfirmDialog from '../../components/dialogs/ConfirmDialog';
import QuestionDetailsDialog from '../../components/dialogs/QuestionDetailsDialog';

const ReadingQuestionList = () => {
  const { readingId, questionSetId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { passage } = location.state || {};

  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [questionSetType, setQuestionSetType] = useState('');
  const [questionInterval, setQuestionInterval] = useState('');
  const [questionCount, setQuestionCount] = useState(0);
  const [answerOptions, setAnswerOptions] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [viewDetails, setViewDetails] = useState(null);

  // Fetch Question Set Details
  const fetchQuestionSetDetails = useCallback(async () => {
    if (!passage) return;
    try {
      const response = await getQuestionSetById(readingId, passage, questionSetId);
      const data = response.data;
      if (data) {
        if (data.type) setQuestionSetType(data.type);
        if (data.questionInterval) setQuestionInterval(data.questionInterval);
        if (data.questionCount) setQuestionCount(data.questionCount);
        if (data.answerOptions) setAnswerOptions(data.answerOptions);
      }
    } catch (error) {
      console.error('Failed to fetch question set details:', error);
    }
  }, [readingId, passage, questionSetId]);

  // Fetch Questions
  const fetchQuestions = useCallback(async () => {
    try {
      const response = await getAllQuestions(readingId, passage, questionSetId);
      const data = response.data;
      setQuestions(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to fetch questions:', error);
      setQuestions([]);
    } finally {
      setLoading(false);
    }
  }, [readingId, passage, questionSetId]);

  useEffect(() => {
    if (passage) {
      fetchQuestionSetDetails();
      fetchQuestions();
    } else {
      setLoading(false);
    }
  }, [passage, fetchQuestionSetDetails, fetchQuestions]);

  // Handlers
  const handleEdit = (questionId) => {
    navigate(
      `/readings/${readingId}/${passage}/question-sets/${questionSetId}/questions/${questionId}/edit`,
      { state: { passage, type: questionSetType, answerOptions } }
    );
  };

  const handleCreate = () => {
    const start = parseInt((questionInterval || '1').split('-')[0], 10) || 1;
    const nextNumber = start + questions.length;
    const remaining = Math.max(questionCount - questions.length, 0);
    navigate(
      `/readings/${readingId}/${passage}/question-sets/${questionSetId}/questions/new`,
      { state: { passage, type: questionSetType, questionCount: remaining, startQuestionNumber: nextNumber, answerOptions } }
    );
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteQuestion(readingId, passage, deleteTarget._id);
      setQuestions((prev) => prev.filter((q) => q._id !== deleteTarget._id));
      showToast('Question deleted successfully.', 'success');
    } catch (error) {
      console.error('Failed to delete question:', error);
      showToast('An error occurred while deleting the question.', 'error');
    } finally {
      setDeleteTarget(null);
    }
  };

  // Columns
  const columns = [
    {
      field: 'questionNumber',
      headerName: 'Question #',
      flex: 0.5,
      renderCell: (params) => (
        <Box sx={{ textAlign: 'center' }}>
          <Avatar
            sx={{
              backgroundColor: 'primary.main',
              width: 32,
              height: 32,
              fontSize: '0.875rem',
              mx: 'auto',
            }}
          >
            {params.value}
          </Avatar>
        </Box>
      ),
    },
    {
      field: 'question',
      headerName: 'Question Text',
      flex: 2,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {params.value || params.row.questionText || 'No question text available'}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="View Details">
            <IconButton
              onClick={() => setViewDetails(params.row)}
              sx={{ color: 'info.main', '&:hover': { backgroundColor: 'info.50' } }}
            >
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit Question">
            <IconButton
              onClick={() => handleEdit(params.row._id)}
              sx={{ color: 'primary.main', '&:hover': { backgroundColor: 'primary.50' } }}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete Question">
            <IconButton
              onClick={() => setDeleteTarget(params.row)}
              sx={{ color: 'error.main', '&:hover': { backgroundColor: 'error.50' } }}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  // Loading state
  if (loading) {
    return <EmptyState loading loadingText="Loading questions..." />;
  }

  // Navigation error state
  if (!passage) {
    return (
      <EmptyState
        icon={<QuizIcon sx={{ fontSize: 32 }} />}
        iconColor="error.main"
        title="Navigation Error"
        message="Passage is undefined. Please navigate correctly."
      />
    );
  }

  return (
    <Box>
      <BreadcrumbsNav
        links={[
          { label: 'Readings', onClick: () => navigate('/readings') },
          { label: 'Question Sets', onClick: () => navigate(`/readings/${readingId}/question-sets`, { state: { passage } }) },
        ]}
        current="Questions"
      />
      <PageHeader
        title="Questions Management"
        subtitle={`Manage questions for ${passage} - Question Set ${questionSetId}`}
        icon={<QuizIcon />}
        iconColor="primary.main"
        actions={
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Chip
              label={`${questions.length} Questions`}
              color="primary"
              variant="outlined"
              sx={{ fontWeight: 500 }}
            />
            {questionSetType && (
              <Chip
                label={questionSetType}
                color="info"
                variant="outlined"
                sx={{ fontWeight: 500, fontFamily: 'monospace' }}
              />
            )}
            <ModernButton onClick={handleCreate} startIcon={<AddIcon />} variant="contained">
              Create Question
            </ModernButton>
          </Box>
        }
      />

      <QuestionSetInfoCard
        icon={<MenuBookIcon />}
        sectionLabel="Passage"
        sectionValue={passage}
        questionType={questionSetType}
        questionInterval={questionInterval}
        questionCount={questionCount}
      />

      {questions.length === 0 ? (
        <EmptyState
          icon={<QuizIcon sx={{ fontSize: 32 }} />}
          title="No Questions Found"
          message="No questions found for this question set. Create your first question to get started."
          actionLabel="Create First Question"
          actionIcon={<AddIcon />}
          onAction={handleCreate}
        />
      ) : (
        <ModernDataGrid
          rows={questions}
          getRowId={(row) => row._id}
          columns={columns}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Confirm Deletion"
        message={`Are you sure you want to delete Question ${deleteTarget?.questionNumber}? This action cannot be undone.`}
        confirmText="Delete"
        confirmColor="error"
      />

      {/* Question Details Dialog */}
      <QuestionDetailsDialog
        question={viewDetails}
        open={Boolean(viewDetails)}
        onClose={() => setViewDetails(null)}
        questionType={questionSetType}
        sectionValue={passage}
      />
    </Box>
  );
};

export default ReadingQuestionList;
